import { PhoneNumber } from "antd-phone-input";
import { GameDto } from "../../games/types";
import { ContentWarning } from "../common/types";

export type JWTDecoded = {
    aud: string;
    azp: string;
    sub: string;
    name: string;
    email: string;
    picture: string;
    family_name: string | undefined;
    given_name: string | undefined;
    exp: number;
    iat: number;
    iss: string;
    nbf: number;
    email_verified: boolean;
}

export type LoginOutput = {
    success: boolean;
    account: Account | undefined;
}

export enum PlayerExperienceLevel {
    NEWBIE = 'NEWBIE',
    BEGINNER = 'BEGINNER',
    ADEPT = 'ADEPT',
    MASTER = 'MASTER'
}

export type SelectableGame = GameDto | { id: string };
export type SelectableContentWarning = ContentWarning | { id: string };

export type PlayerProfile = {
    id: string;
    experience: PlayerExperienceLevel;
    interests: SelectableGame[];
    contentWarnings: SelectableContentWarning[];
    interestedInGm: boolean;
    needWheelchairAccess: boolean;
    hearingImpaired: boolean;
    visuallyImpaired: boolean;
    photosensitivity: boolean;
}

export type PlayerProfileForm = Omit<Omit<PlayerProfile, "interests">, "contentWarnings"> & {
    interests: string[],
    contentWarnings: string[],
}

export const fromPlayerProfileToForm: (profile: PlayerProfile) => PlayerProfileForm = (profile) => {
    return {
        ...profile,
        interests: profile.interests.map((game) => game.id),
        contentWarnings: profile.contentWarnings.map((cw) => cw.id)
    };
}

export const fromNarratorProfileToForm: (profile: NarratorProfile) => NarratorProfileForm = (profile) => {
    return {
        ...profile,
        games: profile.games.map((game) => game.id),
    };
}

export const toPlayerProfileFromForm: (form: PlayerProfileForm) => PlayerProfile = (data) => {
    return {
        ...data,
        interests: (data.interests || []).map((id) => { return { id } }),
        contentWarnings: (data.contentWarnings || []).map((id) => { return { id } })
    };
}

export const toNarrativeProfileFromForm: (form: NarratorProfileForm) => NarratorProfile = (data) => {
    return {
        ...data,
        games: (data.games || []).map((id) => { return { id } }),
    };
}

export type Account = {
    id: string;
    name: string;
    nickname: string;
    email: string;
    cellphone: string;
    picture: string;
    status: 'VERIFIED' | 'NEW';
    profile: PlayerProfile | undefined;
    narrator: NarratorProfile | undefined;
    venues: object[];
    admin: boolean;
    organizer: OrganizerDto | undefined;
}

export type NarratorProfile = {
    id: string;
    experience: PlayerExperienceLevel;
    games: SelectableGame[];
}

export type NarratorProfileForm = Omit<NarratorProfile, "games"> & {
    games: string[]
}

export type VerificationForm = {
    userId: string;
    nickname: string;
    mobile: PhoneNumber;
    terms: boolean;
    privacy: boolean;
}

export type VerificationFormBe = Omit<VerificationForm, 'mobile'> & {
    captcha: string
    mobile: string
};

export type SmsRequest = {
    captcha: string;
    number: string;
    text: string;
}

export type TwoFactorAuthRequest = {
    captcha: string;
    number: string;
}

export type TwoFactorAuthStatusResponse = {
    refCode: string;
    attemptsLeft: number;
    status: 'PENDING' | 'VERIFIED';
}

export type UserAccess = {
    canAssignAdministrators: boolean;
    isOrganizer: boolean;
}

export type UserSummary = {
    id: string;
    name: string;
    nickname: string;
    email: string;
    cellphone: string;
    verified: boolean;
}

export type LinkDto = {
    id: string;
    url: string;
}

export type OrganizerDto = {
    id: string;
    name: string;
    description: string;
    logoId: string;
    logoUrl: string;
    website: LinkDto | undefined;
    facebook: LinkDto | undefined;
    instagram: LinkDto | undefined;
    tiktok: LinkDto | undefined;
    gameMasters: UserSummary[];
}